import { createApi } from "@reduxjs/toolkit/query/react";

import { IItems, TGroup, TRateItem } from "@/interfaces/items.interface";
import { IGroupSchema } from "@/schemas/items-schema";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

export const itemsApi = createApi({
  reducerPath: "itemsApi",
  baseQuery: BaseWithAuth(),
  tagTypes: ["items", "group", "special-rate"],
  endpoints: (builder) => ({
    addItems: builder.mutation({
      query: ({ data }: { data: object }) => ({
        url: `workspace/${getWorkspace()}/item`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["items", "group"],
    }),
    updateItems: builder.mutation({
      query: ({ data, id }: { data: object; id: string }) => ({
        url: `workspace/${getWorkspace()}/item/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["items", "group"],
    }),
    getItems: builder.query<GeneralResponse<IItems[]>, object>({
      query: ({ filter }: { filter: string }) => ({
        url: `workspace/${getWorkspace()}/item${filter ? filter : ""}`,
      }),
      providesTags: ["items"],
    }),
    getSingleItems: builder.query({
      query: (id: string) => ({
        url: `workspace/${getWorkspace()}/item${id}`,
        transformResponse: (response: {
          data: IItems;
          message: string;
        }): object => response?.data,
      }),
    }),
    deleteItems: builder.mutation({
      query: (id: string) => ({
        url: `workspace/${getWorkspace()}/item/${id}`,
        method: "DELETE",
      }),
    }),

    getGroup: builder.query<
      GeneralResponse<TGroup[]> | GeneralSingleResponse<TGroup>,
      object
    >({
      query: ({ filter, groupId }: { filter: string; groupId?: string }) => ({
        url: `workspace/${getWorkspace()}/item/group/${groupId || ""}${filter}`,
      }),
      providesTags: ["group"],
    }),

    mutateItems: builder.mutation({
      query: ({
        data,
        groupId,
        method,
      }: {
        data?: Partial<IGroupSchema>;
        groupId?: string;
        method: string;
      }) => ({
        url: `workspace/${getWorkspace()}/item/group/${groupId || ""}`,
        method: method.toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["group"],
    }),

    linkInvoice: builder.mutation({
      query: ({ data }: { data: object }) => ({
        url: `workspace/${getWorkspace()}/item/link-invoice`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["items"],
    }),
    getSpecialRateItems: builder.query<
      TRateItem,
      {
        customerId?: string;
        projectId?: string;
        type?: "DOCKET" | "ESTIMATION";
        status?: string;
      }
    >({
      query: ({ customerId, projectId, type, status }) => {
        const params = new URLSearchParams();

        if (customerId) {
          params.append("customerId", customerId);
        }
        if (projectId) {
          params.append("projectId", projectId);
        }
        if (type) {
          params.append("type", type);
        }
        if (status) {
          params.append("status", status);
        }

        return {
          url: `workspace/${getWorkspace()}/item/rate?${params.toString()}`,
        };
      },
      providesTags: ["special-rate"],
      transformResponse: (response: APIResponse<TRateItem>) => response?.data,
    }),
  }),
});

export const {
  useAddItemsMutation,
  useGetItemsQuery,
  useDeleteItemsMutation,
  useUpdateItemsMutation,
  useGetSingleItemsQuery,
  useLinkInvoiceMutation,

  // GROUP
  useGetGroupQuery,
  useMutateItemsMutation,
  useGetSpecialRateItemsQuery,
} = itemsApi;
